import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  ClipboardList,
  Users,
  Flag,
  TrendingUp,
  ChevronDown,
  Sparkles,
  Trophy,
  ArrowLeftRight,
  Layers,
  Flower2,
  Waves,
  Sun,
  HeartHandshake,
  MessageCircle,
  Eye,
  Crown,
  AlertCircle,
  RefreshCw,
  GraduationCap
} from "lucide-react";
import {
  RadarChart,
  PolarGrid,
  PolarRadiusAxis,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useTheme } from "../../context/themeContext";
import { useAuth } from "../../context/authContext";
import { BRAND, TOKENS, StatPill, CardShell } from "./dashboardShared";
import Tests from "../../utils/commonTest";
import getTestUIConfig from "../../utils/testUi";
import serverUrl from "../../utils/utilsser";
import PagedResultsCard from "../../components/TestResults/Syndrome";
import CategoricalResultsCard from "../../components/TestResults/Categorical";
import CategoricalResultsPanel from "../../components/TestResults/Categorical";
import IitWellbeingResults from "../../components/iitTest"

const WP_HEX = {
  indigo: "#6366f1",
  amber: "#f5a623",
  sky: "#4dabf7",
  coral: "#ff6b6b",
  green: "#51cf66",
  neutral: "#94a3b8",
};

// One left/right color pair per trait category — cycles if more are added.
const WP_PALETTE = [
  { left: BRAND.purple, right: BRAND.teal },
  { left: BRAND.pink, right: WP_HEX.sky },
  { left: WP_HEX.amber, right: BRAND.purple },
  { left: BRAND.teal, right: WP_HEX.coral },
  { left: WP_HEX.indigo, right: BRAND.pink },
  { left: WP_HEX.green, right: BRAND.purple },
  { left: WP_HEX.sky, right: WP_HEX.amber },
];

const CARD_ACCENTS = ["purple", "pink", "teal"];

const CHAKRA_SEVERITY_TO_CATEGORY = {
  Minimal: "Underactive",
  Mild: "Balanced",
  Moderate: "Balanced",
  Severe: "Overactive",
};

const CHAKRA_CATEGORY_ORDER = ["Underactive", "Balanced", "Overactive"];
const CHAKRA_CATEGORY_COLORS = {
  Underactive: "#3B82F6",
  Balanced: "#22C55E",
  Overactive: "#EF4444",
};
const ADHD_SEVERITY_TO_CATEGORY = {
  Minimal: "Negative Screen",
  Mild: "Negative Screen",
  Moderate: "Positive Screen",
  Severe: "Positive Screen",
};
const ADHD_CATEGORY_ORDER = ["Negative Screen", "Positive Screen"];
const ADHD_CATEGORY_COLORS = {
  "Negative Screen": "#3B82F6",
  "Positive Screen": "#22C55E",
};

function remapToChakraCategories(rawCounts) {
  const totals = { Underactive: 0, Balanced: 0, Overactive: 0 };
  (rawCounts ?? []).forEach((bucket) => {
    const category = CHAKRA_SEVERITY_TO_CATEGORY[bucket.name];
    if (category) totals[category] += bucket.value ?? 0;
  });
  return CHAKRA_CATEGORY_ORDER.map((label) => ({
    name: label,
    value: totals[label],
    color: CHAKRA_CATEGORY_COLORS[label],
  }));
}
function remapToCategories(rawCounts, config) {
  if (!config?.order) return rawCounts; // safety: no config, return raw data

  const totals = Object.fromEntries(config.order.map((l) => [l, 0]));

  (rawCounts ?? []).forEach((bucket) => {
    const category = config.map[bucket.name];
    if (category in totals) totals[category] += bucket.value ?? 0;
    else console.warn("Unmapped bucket:", bucket.name); // helps while testing
  });

  return config.order.map((label) => ({
    name: label,
    value: totals[label],
    color: config.colors[label],
  }));
}

function AnimatedNumber({ value, suffix = "", duration = 700 }) {
  const numeric = typeof value === "number" && !Number.isNaN(value);
  const [display, setDisplay] = useState(numeric ? 0 : value);

  useEffect(() => {
    if (!numeric) {
      setDisplay(value);
      return;
    }
    let frame;
    const start = performance.now();
    const from = 0;
    const to = value;
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => frame && cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration, numeric]);

  if (!numeric) return <>{display}</>;
  return (
    <>
      {display.toFixed(0)}
      {suffix}
    </>
  );
}

function DiscScoreCard({ points, config }) {
  return (
    <div className="space-y-3">
      {config.dimensions.map((d) => {
        const value = points?.[d.key] ?? 0;
        const pct = Math.min(100, (value / config.max) * 100);
        return (
          <div key={d.key}>
            <div className="flex justify-between text-sm font-medium">
              <span>{d.label}</span>
              <span>
                {value}/{config.max}
              </span>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-200">
              <div
                className="h-2 rounded-full bg-purple-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RadialRing({ pct, color, size = 110, stroke = 8, icon: Icon }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="#f3f0fa"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        {Icon ? <Icon size={28} className="text-indigo-500" /> : null}
      </div>
    </div>
  );
}

function RadialCategoryCard({ fields, averages, max = 100 }) {
  return (
    <div className="grid grid-cols-2 gap-x-10 gap-y-10 justify-items-center">
      {fields.map((f) => {
        const pct = Math.round(((averages?.[f.key] ?? 0) / max) * 100);
        return (
          <div key={f.key} className="flex flex-col items-center">
            <RadialRing pct={pct} color={f.color} icon={f.icon} />
            <span className="mt-3 font-bold text-slate-700">{f.label}</span>
            <span className="mt-1 text-slate-500">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function PremiumDial({
  id,
  pct,
  colorFrom,
  colorTo,
  size = 84, // was 116 — shrunk to reduce overall height
  stroke = 7, // was 9
  active,
  icon: Icon,
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (Math.min(100, Math.max(0, pct)) / 100) * c;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="-rotate-90"
        style={{
          filter: active ? `drop-shadow(0 0 8px ${colorTo}99)` : "none",
          transition: "filter 0.3s ease",
          overflow: "visible",
        }}
      >
        <defs>
          <linearGradient
            id={`dial-grad-${id}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor={colorFrom} />
            <stop offset="100%" stopColor={colorTo} />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke="rgba(148,130,255,0.12)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          stroke={`url(#dial-grad-${id})`}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: "stroke-dashoffset 0.9s cubic-bezier(.34,1.56,.64,1)",
            transform: active ? "scale(1.02)" : "scale(1)",
            transformOrigin: `${size / 2}px ${size / 2}px`,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        {Icon ? (
          <Icon size={14} style={{ color: colorTo }} className="mb-0.5" />
        ) : null}
        <span
          className="text-sm font-bold tabular-nums"
          style={{ color: colorTo }}
        >
          <AnimatedNumber value={pct} suffix="%" />
        </span>
      </div>
    </div>
  );
}
function SyndromeThreeCategoryBars({ fields, counts, totalTests = 0 }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(null);

  const rows = fields.map((f) => {
    const raw = counts?.[f.key] ?? 0;
    const pct = totalTests > 0 ? Math.round((raw / totalTests) * 100) : 0;
    return { ...f, raw, pct };
  });

  // chart geometry
  const W = 320;
  const H = 220;
  const padTop = 36; // room for the % label above each bar
  const padBottom = 8;
  const chartH = H - padTop - padBottom;
  const barW = 64;
  const gap = (W - barW * rows.length) / (rows.length + 1);

  return (
    <div className="flex flex-col items-center py-2">
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        <defs>
          {rows.map((f) => {
            const colorFrom = f.colorFrom ?? f.color ?? BRAND.purple;
            const colorTo = f.colorTo ?? f.color ?? BRAND.pink;
            return (
              <linearGradient
                key={`bar-grad-${f.key}`}
                id={`bar-grad-${f.key}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={colorTo} />
                <stop offset="100%" stopColor={colorFrom} />
              </linearGradient>
            );
          })}
        </defs>

        {/* baseline */}
        <line
          x1={0}
          y1={H - padBottom}
          x2={W}
          y2={H - padBottom}
          stroke={isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}
          strokeWidth={1}
        />

        {rows.map((f, i) => {
          const x = gap + i * (barW + gap);
          const barH = Math.max(4, (f.pct / 100) * chartH);
          const y = H - padBottom - barH;
          const isHovered = hovered === f.key;
          const colorTo = f.colorTo ?? f.color ?? BRAND.pink;

          return (
            <g
              key={f.key}
              onMouseEnter={() => setHovered(f.key)}
              onMouseLeave={() => setHovered(null)}
              style={{ cursor: "pointer" }}
            >
              {/* track (full-height ghost bar) */}
              <rect
                x={x}
                y={padTop}
                width={barW}
                height={chartH}
                rx={10}
                fill={isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"}
              />
              {/* filled bar */}
              <rect
                x={x}
                y={y}
                width={barW}
                height={barH}
                rx={10}
                fill={`url(#bar-grad-${f.key})`}
                opacity={isHovered ? 1 : 0.92}
                style={{
                  transition:
                    "height 0.6s cubic-bezier(.34,1.56,.64,1), y 0.6s cubic-bezier(.34,1.56,.64,1), opacity 0.2s ease",
                  filter: isHovered
                    ? `drop-shadow(0 0 8px ${colorTo}99)`
                    : "none",
                }}
              />
              {/* % label above bar */}
              <text
                x={x + barW / 2}
                y={y - 10}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill={colorTo}
              >
                {f.pct}%
              </text>
            </g>
          );
        })}
      </svg>

      {/* labels row below the chart */}
      <div
        className="mt-1 flex w-full justify-center gap-[var(--gap)]"
        style={{ "--gap": `${gap}px` }}
      >
        {rows.map((f) => {
          const isHovered = hovered === f.key;
          const colorTo = f.colorTo ?? f.color ?? BRAND.pink;
          return (
            <div
              key={f.key}
              className="flex flex-col items-center transition-opacity"
              style={{
                width: barW,
                opacity: hovered && !isHovered ? 0.5 : 1,
              }}
              onMouseEnter={() => setHovered(f.key)}
              onMouseLeave={() => setHovered(null)}
            >
              <span
                className={
                  "text-center text-xs font-semibold leading-tight " +
                  t.textPrimary
                }
              >
                {f.label}
              </span>
              <span className={"text-[11px] tabular-nums " + t.textMuted}>
                {f.raw}/{totalTests}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DimensionalResultsGrid({ fields, values, max = 25 }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(null);
  const [pinned, setPinned] = useState(null);
  const displayed = hovered ?? pinned;

  const isFive = fields.length === 5;
  const isSix = fields.length === 6;
  const isOdd = fields.length % 2 !== 0;

  // Only center the last item for odd layouts OTHER than the custom 5-item layout

  const renderDial = (f, i) => {
    const raw = values?.[f.key] ?? 0;
    const fMax = f.max ?? max;
    const pct = fMax > 0 ? Math.round((raw / fMax) * 100) : 0;
    const isActive = displayed === f.key;
    const colorFrom = f.colorFrom ?? f.color ?? BRAND.purple;
    const colorTo = f.colorTo ?? f.color ?? BRAND.pink;
    const isLastOdd = !isFive && !isSix && isOdd && i === fields.length - 1;
    return (
      <div
        key={f.key}
        className={`
    ta-stagger ta-hoverable
    flex flex-col items-center justify-center
  
    rounded-2xl
   
    transition-all duration-300
    cursor-pointer
  `}
        style={{
          animationDelay: `${i * 70}ms`,
        }}
        onMouseEnter={() => setHovered(f.key)}
        onMouseLeave={() => setHovered(null)}
        onClick={() => setPinned((p) => (p === f.key ? null : f.key))}
      >
        <PremiumDial
          id={f.key}
          pct={pct}
          colorFrom={colorFrom}
          colorTo={colorTo}
          active={isActive}
          icon={f.icon}
        />
        <span
          className={
            "text-center text-xs font-semibold leading-tight transition-opacity " +
            t.textPrimary +
            (displayed && !isActive ? " opacity-50" : "")
          }
        >
          {f.label}
        </span>
        <span className={"text-[11px] tabular-nums " + t.textMuted}>
          {raw}/{fMax}
        </span>
      </div>
    );
  };

  // Six-item layout: dials arranged in a circle, with a bigger summary
  // dial (sum of this group's raw/max) sitting in the middle.
  // Uses a fixed pixel canvas (taller than wide) so orbiting labels have
  // room top/bottom and don't collide with content below this component.
  const renderSixCircle = () => {
    // A summed group-average is meaningless once each submission only
    // contributes to one field (dominant-category counting) — sum(counts)
    // is always == totalTests, so that ring was structurally stuck at a
    // constant 100/numFields%. Surface the leading category instead.
    let topField = fields[0];
    let topValue = -Infinity;
    fields.forEach((f) => {
      const v = values?.[f.key] ?? 0;
      if (v > topValue) {
        topValue = v;
        topField = f;
      }
    });
    const topMax = topField.max ?? max;
    const topPct = topMax > 0 ? Math.round((topValue / topMax) * 100) : 0;
    const colorFrom = topField.colorFrom ?? topField.color ?? BRAND.purple;
    const colorTo = topField.colorTo ?? topField.color ?? BRAND.pink;

    const W = 300;
    const H = 340;
    const cx = W / 2;
    const cy = H / 2;
    const radius = Math.min(W, H) * 0.42;

    return (
      <div
        className="mx-auto mb-4"
        style={{ width: W, maxWidth: "100%", height: H, position: "relative" }}
      >
        <div
          className="absolute flex flex-col items-center gap-1"
          style={{ left: cx, top: cy, transform: "translate(-50%, -50%)" }}
        >
          <PremiumDial
            id="group-summary"
            pct={topPct}
            colorFrom={colorFrom}
            colorTo={colorTo}
            size={92}
            stroke={9}
          />
          <span className={"text-[10px] font-semibold " + t.textPrimary}>
            {topField.label}
          </span>
          <span className={"text-[10px] tabular-nums " + t.textMuted}>
            {topValue}/{topMax}
          </span>
        </div>

        {/* Orbiting dials — unchanged */}
        {fields.map((f, i) => {
          const angle = -90 + (360 / fields.length) * i;
          const rad = (angle * Math.PI) / 180;
          const orbitOffsetX = -90;
          const orbitOffsetY = -90;
          const left = cx + radius * Math.cos(rad) + orbitOffsetX;
          const top = cy + radius * Math.sin(rad) + orbitOffsetY;
          const raw = values?.[f.key] ?? 0;
          const fMax = f.max ?? max;
          const pct = fMax > 0 ? Math.round((raw / fMax) * 100) : 0;
          const isActive = displayed === f.key;
          const colorFrom = f.colorFrom ?? f.color ?? BRAND.purple;
          const colorTo = f.colorTo ?? f.color ?? BRAND.pink;

          return (
            <div
              key={f.key}
              className="ta-stagger ta-hoverable absolute flex flex-col items-center gap-0.5 cursor-pointer m-10"
              style={{
                left,
                top,
                transform: "translate(-50%, -50%)",
                animationDelay: `${i * 70}ms`,
              }}
              onMouseEnter={() => setHovered(f.key)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => setPinned((p) => (p === f.key ? null : f.key))}
            >
              <PremiumDial
                id={f.key}
                pct={pct}
                colorFrom={colorFrom}
                colorTo={colorTo}
                active={isActive}
                icon={f.icon}
                size={58}
                stroke={6}
              />
              <span className="block text-center text-[10px] font-semibold leading-tight w-28">
                {f.label}
              </span>
              <span className={"text-[9px] tabular-nums " + t.textMuted}>
                {raw}/{fMax}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      {isSix ? (
        renderSixCircle()
      ) : fields.length === 5 ? (
        <div className="space-y-2">
          {/* Row 1 */}
          <div className="grid grid-cols-2 justify-items-center">
            {fields.slice(0, 2).map((f, i) => renderDial(f, i))}
          </div>

          {/* Row 2 */}
          <div className="flex justify-center">{renderDial(fields[2], 2)}</div>

          {/* Row 3 */}
          <div className="grid grid-cols-2 justify-items-center">
            {fields.slice(3).map((f, i) => renderDial(f, i + 3))}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 justify-items-center">
          {fields.map((f, i) => renderDial(f, i))}
        </div>
      )}
    </>
  );
}

function withRanks(rows) {
  let rank = 0;
  let prevValue = null;
  return rows.map((row, i) => {
    if (row.value !== prevValue) rank = i + 1;
    prevValue = row.value;
    return { ...row, rank };
  });
}

function ArchetypeAngleTick({
  payload,
  fields,
  x,
  y,
  cx,
  cy,
  t,
  hovered,
  setHovered,
  colorOf,
}) {
  const isHovered = hovered === payload.value;
  const color = colorOf(payload.value);
  const dx = (x - cx) * 0.14;
  const dy = (y - cy) * 0.14;
  const field = fields.find((f) => f.key === payload.value);
  const displayLabel = field?.label ?? payload.value;
  return (
    <g
      transform={`translate(${x + dx}, ${y + dy})`}
      onMouseEnter={() => setHovered(payload.value)}
      onMouseLeave={() => setHovered(null)}
      style={{ cursor: "pointer" }}
    >
      <text
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={isHovered ? 12.5 : 11}
        fontWeight={isHovered ? 800 : 600}
        fill={isHovered ? color : t.axisTick}
        style={{ transition: "font-size 0.15s ease, fill 0.15s ease" }}
      >
        {displayLabel}
      </text>
    </g>
  );
}

function ArchetypeRadarDot(props) {
  const { cx, cy, payload, hovered, maxValue, colorOf } = props;
  if (cx == null || cy == null) return null;
  const isHovered = hovered === payload.label;
  const isTop = payload.value === maxValue;
  const color = colorOf(payload.label);
  return (
    <g>
      {isTop && <circle cx={cx} cy={cy} r={9} fill={color} opacity={0.18} />}
      <circle
        cx={cx}
        cy={cy}
        r={isHovered ? 6 : 4}
        fill={color}
        stroke={isHovered ? "#ffffff" : "none"}
        strokeWidth={isHovered ? 2 : 0}
        style={{ transition: "r 0.15s ease" }}
      />
    </g>
  );
}

function ArchetypeRadarPanel({ fields, values, total = 0 }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(null);

  const fieldOf = (key) => fields.find((f) => f.key === key);
  const colorOf = (key) => fieldOf(key)?.color ?? BRAND.purple;

  const chartData = useMemo(
    () => fields.map((f) => ({ label: f.key, value: values?.[f.key] ?? 0 })),
    [fields, values],
  );
  const maxValue = Math.max(...chartData.map((d) => d.value), 1);

  const ranked = useMemo(
    () => withRanks([...chartData].sort((a, b) => b.value - a.value)),
    [chartData],
  );

  // Center label mirrors the donut chart's center-label pattern elsewhere
  // in this file — hover a wedge/row to preview it, otherwise show the
  // top archetype. Fills what would otherwise be dead space in the middle
  // of the radar.
  const centerField = hovered ? fieldOf(hovered) : null;
  const centerValue = hovered ? (values?.[hovered] ?? 0) : maxValue;
  const centerLabel = hovered
    ? (centerField?.label ?? hovered)
    : "Top Archetype";
  const centerColor = hovered ? colorOf(hovered) : BRAND.purple;

  const topRanked = ranked?.slice(0, 5);
  const rows = Math.max(1, Math.ceil(topRanked.length / 2));

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-9">
      <div className="lg:col-span-6">
        <div className="relative h-[260px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} outerRadius="66%">
              <PolarGrid stroke={t.gridStroke} />
              <PolarRadiusAxis
                domain={[0, total]}
                tick={false}
                axisLine={false}
                tickLine={false}
              />
              <PolarAngleAxis
                dataKey="label"
                tick={(props) => (
                  <ArchetypeAngleTick
                    {...props}
                    fields={fields}
                    t={t}
                    hovered={hovered}
                    setHovered={setHovered}
                    colorOf={colorOf}
                  />
                )}
              />
              <Tooltip
                wrapperStyle={{
                  zIndex: 9999,
                  pointerEvents: "none",
                }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload;
                  const field = fieldOf(p.label);
                  return (
                    <div
                      className="rounded-xl border px-3 py-2 text-xs shadow-xl"
                      style={{
                        background: t.tooltipBg,
                        borderColor: t.tooltipBorder,
                      }}
                    >
                      <div
                        className="font-semibold"
                        style={{ color: colorOf(p.label) }}
                      >
                        {field?.label ?? p.label}
                      </div>
                      <div
                        className={isDark ? "text-slate-300" : "text-slate-600"}
                      >
                        {p.value}/{total}
                      </div>
                    </div>
                  );
                }}
              />
              <Radar
                dataKey="value"
                stroke={BRAND.purple}
                strokeWidth={2}
                fill="url(#archetypeRadarFill)"
                fillOpacity={1}
                dot={(props) => (
                  <ArchetypeRadarDot
                    {...props}
                    hovered={hovered}
                    maxValue={maxValue}
                    colorOf={colorOf}
                  />
                )}
                isAnimationActive
              />
              <defs>
                <linearGradient
                  id="archetypeRadarFill"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={BRAND.purple}
                    stopOpacity={0.32}
                  />
                  <stop
                    offset="100%"
                    stopColor={BRAND.pink}
                    stopOpacity={0.12}
                  />
                </linearGradient>
              </defs>
            </RadarChart>
          </ResponsiveContainer>

          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-0.5">
            <span
              className="text-[24px] font-extrabold leading-none tabular-nums transition-colors duration-200 whitespace-nowrap"
              style={{ color: centerColor }}
            >
              {/* <AnimatedNumber value={centerValue} /> */}
            </span>
            {/* <span
              className={
                "max-w-[7rem] truncate text-center text-[9px] font-semibold uppercase tracking-wide " +
                t.textMuted
              }
            >
              {centerLabel}
            </span> */}
          </div>
        </div>
      </div>

      <div className="lg:col-span-3">
        <div
          className={
            "mb-2 text-xs font-semibold uppercase tracking-wider " + t.textFaint
          }
        >
          Ranked
        </div>
        <div className="flex flex-col gap-1.5">
          {topRanked.map((row) => {
            const isHovered = hovered === row.label;
            const isTop = row.rank === 1;
            const field = fieldOf(row.label);
            const color = colorOf(row.label);
            return (
              <button
                key={row.label}
                onMouseEnter={() => setHovered(row.label)}
                onMouseLeave={() => setHovered(null)}
                className={
                  "flex items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-left transition-colors outline-none focus-visible:ring-2 " +
                  (isHovered ? t.surfaceSoft : "")
                }
                style={{ "--tw-ring-color": BRAND.purple }}
              >
                <span
                  className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px] font-bold"
                  style={
                    isTop
                      ? { background: `${color}22`, color }
                      : { background: t.track, color: t.axisTick }
                  }
                >
                  {isTop ? <Trophy size={9} /> : row.rank}
                </span>
                <span
                  className={`block min-w-[65px] flex-1 text-[11px] font-semibold truncate break-words ${t.textPrimary}`}
                >
                  {field?.label ?? row.label}
                </span>
                <span
                  className="shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-bold tabular-nums"
                  style={{ background: `${color}1f`, color }}
                >
                  {row.value}/{total}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

//MBTI

const CHAKRA_ZONE_COLORS = {
  under: "#f5a623",
  balanced: "#22c55e",
  over: "#ef4444",
};

function ChakraZoneBar({ chakra, index, zoneColors, thresholds }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(false);

  const { label, pct, colorFrom, colorTo } = chakra;
  const zone =
    pct < thresholds.low
      ? "under"
      : pct > thresholds.high
        ? "over"
        : "balanced";
  const zoneColor = zoneColors[zone];
  const zoneText =
    zone === "under"
      ? "Under Active"
      : zone === "over"
        ? "Overactive"
        : "Balanced";

  // Dot diameter in px (matches the rendered size below) — used to keep
  // the dot fully inside the track at both 0% and 100%.
  const dotDiameter = hovered ? 14 : 12;
  const dotRadius = dotDiameter / 2;
  const clampedPct = Math.min(100, Math.max(0, pct));

  return (
    <div
      className="ta-hoverable group cursor-default"
      style={{ animationDelay: `${index * 60}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span
            className={
              "text-sm font-semibold transition-colors " +
              (hovered
                ? t.textPrimary
                : isDark
                  ? "text-slate-200"
                  : "text-slate-700")
            }
          >
            {label}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
            style={{ background: `${zoneColor}1f`, color: zoneColor }}
          >
            {zoneText}
          </span>
          <span
            className="text-sm font-bold tabular-nums"
            style={{ color: zoneColor }}
          >
            {hovered ? (
              <AnimatedNumber value={pct} suffix="%" />
            ) : (
              `${chakra.raw}/${chakra.total}`
            )}
          </span>
        </div>
      </div>

      {/* Outer track — no overflow-hidden here, so the dot is never clipped */}
      <div className="relative h-3 w-full">
        {/* Inner layer: base track color + tint bands + dividers, clipped */}
        <div
          className="absolute inset-0 overflow-hidden rounded-full"
          style={{
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
          }}
        >
          {/* zone tint bands */}
          <div className="absolute inset-0 flex">
            <div
              style={{
                width: `${thresholds.low}%`,
                background: `${zoneColors.under}14`,
              }}
            />
            <div
              style={{
                width: `${thresholds.high - thresholds.low}%`,
                background: `${zoneColors.balanced}14`,
              }}
            />
            <div
              style={{
                width: `${100 - thresholds.high}%`,
                background: `${zoneColors.over}14`,
              }}
            />
          </div>

          {/* threshold dividers */}
          <div
            className="absolute inset-y-0 border-l border-dashed"
            style={{
              left: `${thresholds.low}%`,
              borderColor: isDark
                ? "rgba(255,255,255,0.18)"
                : "rgba(0,0,0,0.14)",
            }}
          />
          <div
            className="absolute inset-y-0 border-l border-dashed"
            style={{
              left: `${thresholds.high}%`,
              borderColor: isDark
                ? "rgba(255,255,255,0.18)"
                : "rgba(0,0,0,0.14)",
            }}
          />
        </div>

        {/* filled progress dot — sits on the un-clipped outer div.
            Interpolates between dotRadius (at 0%) and 100%-dotRadius
            (at 100%) so it's always fully inside the track. */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full border-2 border-white transition-all duration-300"
          style={{
            left: `calc(${dotRadius}px + (100% - ${dotDiameter}px) * ${clampedPct} / 100)`,
            width: dotDiameter,
            height: dotDiameter,
            background: colorTo ?? zoneColor,
            boxShadow: `0 0 ${hovered ? 16 : 11}px ${colorTo ?? zoneColor}dd`,
          }}
        />
      </div>
    </div>
  );
}

function ChakraZoneBars({
  fields,
  counts, // e.g. { Sacral_Chakra: 1, Solar_Plexus_Chakra: 0, ... }
  totalTests, // e.g. 1
  thresholds = { low: 33, high: 68 },
}) {
  const { isDark } = useTheme();

  const rows = useMemo(() => {
    return fields
      .map((f) => {
        const raw = counts?.[f.key] ?? 0;
        const total = totalTests || 0;
        const pct = total > 0 ? Math.round((raw / total) * 100) : 0;
        return { ...f, raw, total, pct };
      })
      .sort((a, b) => b.pct - a.pct);
  }, [fields, counts, totalTests]);

  return (
    <div>
      <div className="mb-5 flex flex-col gap-4">
        {rows.map((chakra, i) => (
          <ChakraZoneBar
            key={chakra.key}
            chakra={chakra}
            index={i}
            zoneColors={CHAKRA_ZONE_COLORS}
            thresholds={thresholds}
          />
        ))}
      </div>

      <div
        className="flex flex-wrap items-center justify-center gap-2 border-t pt-4"
        style={{
          borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        }}
      >
        {[
          { key: "under", label: "Under Active" },
          { key: "balanced", label: "Balanced" },
          { key: "over", label: "Overactive" },
        ].map((z) => (
          <span
            key={z.key}
            className="flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
            style={{
              background: `${CHAKRA_ZONE_COLORS[z.key]}1a`,
              color: CHAKRA_ZONE_COLORS[z.key],
            }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: CHAKRA_ZONE_COLORS[z.key] }}
            />
            {z.label}
          </span>
        ))}
      </div>
    </div>
  );
}


const SUBSCALE_LEVEL_COLORS = {
  Normal: "#22C55E",
  Mild: "#3B82F6",
  Moderate: "#F59E0B",
  Severe: "#EF4444",
  "Extremely Severe": "#991B1B",
};

function SubscaleResultsPanel({ categories, totalTests }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  return (
    <div className="flex flex-col gap-6">
      {categories.map((cat, idx) => (
        <div key={cat.key} className="ta-stagger" style={{ animationDelay: `${idx * 60}ms` }}>
          <div className="mb-2 flex items-center justify-between">
            <span className={"text-sm font-semibold " + t.textPrimary}>{cat.name}</span>
            <span className={"text-xs font-semibold tabular-nums " + t.textMuted}>
              {cat.highCount}/{totalTests} high
            </span>
          </div>

          {/* stacked bar: one segment per severity level */}
          <div className={"flex h-3 w-full overflow-hidden rounded-full " + t.track}>
            {Object.entries(cat.counts).map(([level, count]) =>
              count > 0 ? (
                <div
                  key={level}
                  title={`${level}: ${count}`}
                  style={{
                    width: `${(count / totalTests) * 100}%`,
                    background: SUBSCALE_LEVEL_COLORS[level] ?? "#94a3b8",
                  }}
                />
              ) : null,
            )}
          </div>

          {/* legend with counts */}
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
            {Object.entries(cat.counts).map(([level, count]) => (
              <span key={level} className={"flex items-center gap-1 text-[11px] " + t.textMuted}>
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ background: SUBSCALE_LEVEL_COLORS[level] ?? "#94a3b8" }}
                />
                {level} <b className="tabular-nums">{count}</b>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// helper

const getSlugFromUrl = (url = "") => {
  return url.split("/").filter(Boolean).pop();
};

function getPathname(url) {
  try {
    return new URL(url, window.location.origin).pathname.replace(/\/+$/, "");
  } catch {
    return url ?? "";
  }
}

function PremiumStyles() {
  return (
    <style>{`
      @keyframes ta-shimmer-sweep {
        0% { transform: translateX(-160%); }
        100% { transform: translateX(160%); }
      }
      @keyframes ta-fade-slide-up {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .ta-premium-bar { position: relative; overflow: visible; }
      .ta-premium-bar::after {
        content: "";
        position: absolute;
        top: 0; left: 0; height: 100%; width: 45%;
        background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
        opacity: 0;
        pointer-events: none;
      }
      .ta-hoverable:hover .ta-premium-bar::after {
        opacity: 1;
        animation: ta-shimmer-sweep 1.1s ease;
      }
      .ta-stagger {
        animation: ta-fade-slide-up 0.45s ease both;
      }
      .ta-card-lift {
        transition: transform 0.25s ease, box-shadow 0.25s ease;
      }
      .ta-card-lift:hover {
        transform: translateY(-3px);
        box-shadow: 0 14px 34px -12px rgba(0,0,0,0.25);
      }
       @keyframes ta-highlight-pulse {
  0% { background-color: rgba(239,93,168,0.22); box-shadow: 0 0 0 2px rgba(239,93,168,0.45); }
  100% { background-color: rgba(239,93,168,0); box-shadow: 0 0 0 2px rgba(239,93,168,0); }
}
.ta-highlight-flash {
  animation: ta-highlight-pulse 1.6s ease-out;
  border-radius: 12px;
}
  @keyframes ta-plus-one-pop {
  0%   { opacity: 0; transform: translate(-50%, 4px) scale(0.7); }
  15%  { opacity: 1; transform: translate(-50%, -4px) scale(1.1); }
  30%  { transform: translate(-50%, -8px) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -26px) scale(1); }
}
.ta-plus-one-badge {
  position: absolute;
  top: -10px;
  left: 50%;
  font-size: 11px;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(90deg, #ef5da8, #8b7cf6);
  padding: 2px 8px;
  border-radius: 9999px;
  box-shadow: 0 4px 12px rgba(139,124,246,0.5);
  animation: ta-plus-one-pop 1.6s ease-out forwards;
  pointer-events: none;
  z-index: 30;
  white-space: nowrap;
}
    `}</style>
  );
}

// ---------------------------------------------------------------------------
// Box 1 — Most taken tests
// ---------------------------------------------------------------------------

const truncateWords = (text, maxWords) => {
  const words = text.trim().split(/\s+/);

  if (words.length <= maxWords) return text;

  return words.slice(0, maxWords).join(" ") + "...";
};

// pagination

function PremiumPagination({ page, totalPages, onChange, rangeLabel }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  // Build a compact page list with ellipses, e.g. 1 2 3 ... 8
  const pages = useMemo(() => {
    const current = page + 1; // 1-indexed for display
    const total = totalPages;
    const delta = 1;
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  }, [page, totalPages]);

  // Early return now happens AFTER all hooks have run — satisfies
  // react-hooks/rules-of-hooks.

  const NavButton = ({ direction, disabled }) => (
    <button
      onClick={() => onChange(direction === "prev" ? page - 1 : page + 1)}
      disabled={disabled}
      aria-label={direction === "prev" ? "Previous page" : "Next page"}
      className={
        "flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-200 " +
        t.pillBorder +
        " " +
        (disabled
          ? "cursor-not-allowed opacity-30 " + t.surfaceSoft
          : t.surfaceSoft + " " + t.surfaceHover + " active:scale-90")
      }
    >
      <ChevronDown
        size={13}
        className={t.textMuted}
        style={{
          transform: direction === "prev" ? "rotate(90deg)" : "rotate(-90deg)",
        }}
      />
    </button>
  );
  if (totalPages <= 1) return null;

  return (
    <div
      className="mt-6 flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between"
      style={{
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      }}
    >
      {rangeLabel ? (
        <span
          className={"text-[11px] font-medium tracking-wide " + t.textMuted}
        >
          {rangeLabel}
        </span>
      ) : (
        <span />
      )}

      <div className="flex items-center gap-1.5 self-center sm:self-auto">
        <NavButton direction="prev" disabled={page === 0} />

        <div className="flex items-center gap-1">
          {pages.map((p, i) =>
            p === "..." ? (
              <span
                key={`dots-${i}`}
                className={"px-1.5 text-xs " + t.textMuted}
              >
                ···
              </span>
            ) : (
              <button
                key={p}
                onClick={() => onChange(p - 1)}
                className="relative flex h-8 min-w-8 items-center justify-center rounded-full px-2.5 text-xs font-semibold transition-all duration-200 active:scale-90 "
                style={
                  page === p - 1
                    ? {
                        color: "#fff",
                        background: `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.purple})`,
                        boxShadow: `0 3px 12px ${BRAND.pink}66, 0 0 0 1px ${BRAND.pink}33`,
                      }
                    : undefined
                }
              >
                {page !== p - 1 && (
                  <span
                    className={
                      "absolute inset-0 rounded-full border transition-colors " +
                      t.pillBorder +
                      " " +
                      t.surfaceSoft
                    }
                  />
                )}
                <span
                  className={"relative " + (page === p - 1 ? "" : t.textMuted)}
                >
                  {p}
                </span>
              </button>
            ),
          )}
        </div>

        <NavButton direction="next" disabled={page >= totalPages - 1} />
      </div>
    </div>
  );
}

// Most taken Tests
function MostTakenTests({
  testData,
  loading,
  viewall = false,
  selectedTest,
  onSelectTest,
  pendingHighlight,
  onHighlightConsumed,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(null);
  const totalTestTaken = testData?.data?.summary?.[0]?.totalTestsTaken;

  // --- pagination (frontend-only, viewall mode only) ---
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(0);

  const allTests = testData?.data?.testCounts ?? [];
  const totalPages = viewall
    ? Math.max(1, Math.ceil(allTests.length / PAGE_SIZE))
    : 1;

  // reset to first page if data shrinks/changes so we don't get stuck on an empty page

  const visibleTests = viewall
    ? allTests.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
    : allTests.slice(0, 5);
  // --- end pagination ---
  const rowRefs = useRef(new Map());
  const [highlightedTest, setHighlightedTest] = useState(null);
  const allTestsRef = useRef(allTests);
  allTestsRef.current = allTests;
  const [pendingScrollName, setPendingScrollName] = useState(null);

  const findAndHighlight = (redirectTo) => {
    if (!viewall || !redirectTo) return;
    const targetPath = getPathname(redirectTo);
    const list = allTestsRef.current;
    const index = list.findIndex((t) => getPathname(t.link) === targetPath);
    if (index === -1) return;

    const targetPage = Math.floor(index / PAGE_SIZE);
    const testName = list[index].testName;

    setPage(targetPage); // may or may not change page — doesn't matter now
    setPendingScrollName(testName); // this ALWAYS triggers a render since it's a fresh value
  };

  // Fires after the page state (and thus the row list) has actually re-rendered
  useEffect(() => {
    if (!pendingScrollName) return;
    const el = rowRefs.current.get(pendingScrollName);
    if (!el) return; // row not mounted for this page yet — effect will re-fire once it is
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedTest(pendingScrollName);
    setPendingScrollName(null);
    const t = setTimeout(() => setHighlightedTest(null), 1600);
    return () => clearTimeout(t);
  }, [pendingScrollName, page, visibleTests]);

  useEffect(() => {
    if (!viewall || !pendingHighlight || allTests.length === 0) return;
    findAndHighlight(pendingHighlight);
    onHighlightConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewall, pendingHighlight, allTests.length]);

  return (
    <CardShell accent="pink" className="ta-card-lift flex flex-col">
      <div className="mb-1 flex items-center justify-between">
        <h2 className={"text-xl font-bold " + t.textPrimary}>
          {viewall ? "All Test Taken " : "Most Taken Tests"}
        </h2>
      </div>
      <p className={"mb-6 text-sm " + t.textMuted}>
        Share of test sessions by assessment type
      </p>

      <div className="flex flex-1 flex-col gap-5">
        {allTests.length === 0 ? (
          viewall ? (
            <div className="flex flex-1 flex-col items-center justify-center py-10 text-gray-500">
              <ClipboardList className="mb-3 h-12 w-12 opacity-40" />

              <p className="text-lg font-semibold">No tests taken yet</p>

              <p className="mt-1 text-center text-sm text-gray-400">
                Your completed assessments will appear here.
              </p>
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className="text-base font-medium text-gray-500">
                No Test Taken Yet
              </p>
            </div>
          )
        ) : (
          visibleTests.map((x, idx) => {
            const isHovered = hovered === x.testName;
            const isSelected = selectedTest === x.testName;

            const percent =
              totalTestTaken > 0 ? (x.count / totalTestTaken) * 100 : 0;
            const isHighlighted = highlightedTest === x.testName;

            return (
              <div
                key={x.testName}
                ref={(el) => {
                  if (el) rowRefs.current.set(x.testName, el);
                  else rowRefs.current.delete(x.testName);
                }}
                className={
                  "ta-hoverable ta-stagger group cursor-pointer " +
                  (isHighlighted ? "ta-highlight-flash" : "")
                }
                style={{ animationDelay: `${idx * 45}ms` }}
                onMouseEnter={() => setHovered(x.testName)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onSelectTest?.(x.testName)}
                role="button"
                tabIndex={0}
                title="View results for this test"
              >
                <div className="mb-2 flex items-center justify-between text-sm ">
                  <span
                    className={
                      "font-medium transition-colors " +
                      (isSelected
                        ? t.textPrimary
                        : isHovered
                          ? t.textPrimary
                          : isDark
                            ? "text-slate-200"
                            : "text-slate-700")
                    }
                  >
                    {truncateWords(x.testName, 8)}
                  </span>

                  <span
                    className="font-semibold tabular-nums"
                    style={{ color: x.color }}
                  >
                    {loading ? (
                      "..."
                    ) : (
                      <AnimatedNumber value={percent} suffix="%" />
                    )}
                  </span>
                </div>

                <div
                  className={
                    "h-2.5 w-full overflow-hidden rounded-full transition-colors " +
                    t.track
                  }
                >
                  <div
                    className="ta-premium-bar h-full rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${percent}%`,
                      background: "linear-gradient(90deg, #ef5da899, #ef5da8)",
                      boxShadow: isHovered ? "0 0 14px #ef5da888" : "none",
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      {viewall && allTests.length > PAGE_SIZE && (
        <PremiumPagination
          page={page}
          totalPages={totalPages}
          onChange={setPage}
          rangeLabel={`Showing ${page * PAGE_SIZE + 1}–${Math.min(
            (page + 1) * PAGE_SIZE,
            allTests.length,
          )} of ${allTests.length}`}
        />
      )}
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Box 2 — Test taken: results (donut) — unchanged "standard" logic
// ---------------------------------------------------------------------------

function polarToCartesian(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function donutArcPath(cx, cy, rOuter, rInner, startAngle, endAngle) {
  const startOuter = polarToCartesian(cx, cy, rOuter, endAngle);
  const endOuter = polarToCartesian(cx, cy, rOuter, startAngle);
  const startInner = polarToCartesian(cx, cy, rInner, endAngle);
  const endInner = polarToCartesian(cx, cy, rInner, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? 0 : 1;
  return [
    `M ${startOuter.x} ${startOuter.y}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 0 ${endOuter.x} ${endOuter.y}`,
    `L ${endInner.x} ${endInner.y}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 1 ${startInner.x} ${startInner.y}`,
    "Z",
  ].join(" ");
}

function ResultsFooterCount({ count, label = "test" }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <div
      className="mt-5 flex items-center justify-center border-t pt-3 text-[12px] font-medium"
      style={{
        borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        color: t.textMuted,
      }}
    >
      Results from <AnimatedNumber value={count ?? 0} />
      &nbsp;{(count ?? 0) === 1 ? "participant" : "participants"}
    </div>
  );
}

function TestResultsDonut({
  Severity2,
  testData,
  loading,
  error,
  selectedTest,
  setSelectedTest,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [active, setActive] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const testDropdownRef = useRef(null);

  // Close the dropdown on any click outside of it.
  useEffect(() => {
    if (!testOpen) return;
    const handleClickOutside = (e) => {
      if (
        testDropdownRef.current &&
        !testDropdownRef.current.contains(e.target)
      ) {
        setTestOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [testOpen]);

  const selectedTestItem = useMemo(() => {
    return testData?.data?.testCounts?.find(
      (item) => item.testName === selectedTest,
    );
  }, [testData, selectedTest]);
  const testNames = useMemo(() => {
    const names =
      testData?.data?.testCounts?.map((item) => item.testName) ?? [];
    // Always alphabetical, and independent of selection/order in testCounts,
    // so picking a test never reshuffles the list.
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [testData]);

  const selectedSlug = getSlugFromUrl(selectedTestItem?.link);

  const isSupportedTest = Tests?.common?.some(
    (item) => getSlugFromUrl(item.link) === selectedSlug,
  );

  const isChakraTest = Tests?.chakra?.some(
    (item) => getSlugFromUrl(item.link) === selectedSlug,
  );
  const adhdTestConfig = Tests?.adhd?.find(
    (item) => getSlugFromUrl(item.link) === selectedSlug,
  );
  const isADHDTest = !!adhdTestConfig;

  const selectedTestClean = (selectedTest ?? "").trim();
  const uiConfig = getTestUIConfig(selectedTestClean) ?? { type: "scored" };
  const testEntry = Severity2?.data?.byTest?.[selectedTestClean];

  const activeData = useMemo(() => {
    const rawCounts = testEntry?.buckets ?? Severity2?.data?.testCounts ?? [];

    const testCounts = isChakraTest
      ? remapToChakraCategories(rawCounts)
      : isADHDTest
        ? remapToCategories(rawCounts,adhdTestConfig)
        : rawCounts;

    return {
      totalTests: testEntry?.totalTests ?? Severity2?.data?.totalTests ?? 0,
      testCounts,
    };
  }, [Severity2, isChakraTest, isADHDTest, adhdTestConfig,testEntry]);

  const Header = (
    <>
      <div className="mb-1 flex items-start justify-between gap-3">
        <div>
          <h2 className={"text-xl font-bold " + t.textPrimary}>
            Test Taken: Results
          </h2>
        </div>

        {testNames.length === 0 ? (
          <span
            className={
              "shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            No tests available
          </span>
        ) : (
          <div className="relative shrink-0" ref={testDropdownRef}>
            <button
              onClick={() => setTestOpen((o) => !o)}
              className={
                "flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-colors " +
                t.pillBorder +
                " " +
                t.surfaceSoft +
                " " +
                t.textMuted +
                " " +
                t.surfaceHover
              }
            >
              {truncateWords(selectedTest, 4)}
              <ChevronDown
                size={13}
                className={
                  "transition-transform duration-200 " +
                  (testOpen ? "rotate-180" : "")
                }
              />
            </button>
            {testOpen && (
              <div
                className={
                  "absolute right-0 z-10 mt-2 max-h-64 w-56 overflow-y-auto rounded-xl border py-1 shadow-xl " +
                  t.card +
                  " " +
                  t.pillBorder
                }
              >
                {testNames.map((name) => (
                  <button
                    key={name}
                    onClick={() => {
                      setSelectedTest(name);
                      setTestOpen(false);
                      setActive(null);
                      setPinned(null);
                    }}
                    className={
                      "flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-xs font-medium transition-colors " +
                      t.surfaceHover +
                      " " +
                      (selectedTest === name ? t.textPrimary : t.textMuted)
                    }
                  >
                    <span className="truncate">{name}</span>
                    {selectedTest === name ? (
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full"
                        style={{ background: BRAND.teal }}
                      />
                    ) : null}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <p className={"mb-4 text-sm " + t.textMuted}>
        {testNames.length === 0
          ? "No tests available yet"
          : `Outcome distribution for ${selectedTest}`}
      </p>
    </>
  );
  const total = activeData?.totalTests;

  if (loading) {
    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        {/* header skeleton */}
        <div className="mb-1 flex items-start justify-between gap-3">
          <div
            className={"h-6 w-40 animate-pulse rounded-md " + t.surfaceSoft}
          />
          <div
            className={
              "h-7 w-24 shrink-0 animate-pulse rounded-full " + t.surfaceSoft
            }
          />
        </div>
        <div
          className={"mb-4 h-4 w-52 animate-pulse rounded-md " + t.surfaceSoft}
        />

        {/* body skeleton: donut + legend, mirrors the archetype/donut layout */}
        <div className="flex flex-1 flex-col items-center justify-center gap-6 py-4 sm:flex-row">
          <div className="relative h-56 w-56 shrink-0">
            <div
              className={
                "h-full w-full animate-pulse rounded-full " + t.surfaceSoft
              }
              style={{
                WebkitMask:
                  "radial-gradient(farthest-side, transparent calc(100% - 26px), #000 calc(100% - 26px))",
                mask: "radial-gradient(farthest-side, transparent calc(100% - 26px), #000 calc(100% - 26px))",
              }}
            />
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:min-w-[180px]">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-8 rounded-xl px-3 py-2"
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className={
                      "h-2.5 w-2.5 shrink-0 animate-pulse rounded-full " +
                      t.surfaceSoft
                    }
                  />
                  <span
                    className={
                      "h-3.5 animate-pulse rounded-md " + t.surfaceSoft
                    }
                    style={{ width: 70 + i * 12 }}
                  />
                </div>
                <span
                  className={
                    "h-3.5 w-8 animate-pulse rounded-md " + t.surfaceSoft
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-16 text-center">
          <div
            className={
              "flex h-11 w-11 items-center justify-center rounded-full " +
              (isDark ? "bg-red-500/10" : "bg-red-50")
            }
          >
            <AlertCircle
              size={20}
              className={isDark ? "text-red-400" : "text-red-500"}
            />
          </div>
          <p className={"text-sm font-medium " + t.textPrimary}>
            Couldn't load your results
          </p>
          <p className={"max-w-[220px] text-xs " + t.textMuted}>
            Something went wrong while fetching this data. Please try again.
          </p>
        </div>
      </CardShell>
    );
  }
  // console.log(uiConfig.type);
  // Shared header (test picker dropdown) — unchanged, just factored so both

  if (uiConfig.type == "syndrome") {
    const hasData = testEntry?.percentages || testEntry?.averages;
    const fieldsConfig = uiConfig?.fields ?? [];
    const isThreeCategory = fieldsConfig.length === 3;

    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        {Header}
        {!hasData ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
            <p className={"text-sm font-medium " + t.textMuted}>
              No results recorded yet for this test.
            </p>
          </div>
        ) : isThreeCategory ? (
          <>
            <SyndromeThreeCategoryBars
              fields={fieldsConfig}
              counts={testEntry.counts ?? testEntry.averages}
              totalTests={testEntry.totalTests}
            />
            <ResultsFooterCount count={testEntry?.totalTests} />
          </>
        ) : (
          <>
            <PagedResultsCard
              testName={selectedTestClean}
              result={{
                ...testEntry,
                averages: testEntry.counts ?? testEntry.averages,
              }}
              fieldsConfig={fieldsConfig}
              maxPerField={
                testEntry.counts
                  ? testEntry.totalTests || 1
                  : (uiConfig?.max ?? 25)
              }
              theme={isDark ? "dark" : "light"}
            />
            <ResultsFooterCount count={testEntry?.totalTests} />
          </>
        )}
      </CardShell>
    );
  }
  if (uiConfig.type === "archetype") {
    const archTotal = testEntry?.totalTests || 0;

    let cursor = 0;
    const archArcs = (testEntry?.archetypes ?? []).map((a) => {
      const normalize = (s) => s?.toLowerCase().replace(/[\s_-]/g, "");
      const field = uiConfig.fields?.find(
        (f) =>
          normalize(f.key) === normalize(a.name) ||
          normalize(f.label) === normalize(a.name),
      );
      const color = field?.color ?? BRAND.purple;
      const value = a.count;

      const start = archTotal > 0 ? (cursor / archTotal) * 360 : 0;
      cursor += value;
      let end = archTotal > 0 ? (cursor / archTotal) * 360 : 0;

      // A segment that spans the whole circle (start ≈ end ≈ same point after
      // wrapping) produces a degenerate SVG arc that renders with no fill.
      // Clamp it just shy of a full sweep so start and end are distinct points.
      if (end - start >= 360) end = start + 359.99;
      if (start === 0 && end >= 360) end = 359.99;

      return { name: field?.label ?? a.name, value, start, end, color };
    });
    const archDisplayed = active ?? pinned;
    const archActiveSeg = archArcs.find((a) => a.name === archDisplayed);

    const archArcsToDraw = (() => {
      const withValue = archArcs.filter((a) => a.value > 0);
      if (!archDisplayed) return withValue;
      const rest = withValue.filter((a) => a.name !== archDisplayed);
      const activeOne = withValue.filter((a) => a.name === archDisplayed);
      return [...rest, ...activeOne];
    })();

    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        {Header}

        {!testEntry?.averages || archArcs.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
            <p className={"text-sm font-medium " + t.textMuted}>
              No results recorded yet for this test.
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-1 flex-col items-center justify-center gap-6 sm:flex-row">
              <div className="relative h-56 w-56 shrink-0">
                <svg viewBox="0 0 200 200" className="h-full w-full">
                  <defs>
                    {archArcsToDraw.map((a) => (
                      <filter
                        key={`glow-${a.name}`}
                        id={`glow-${a.name}`}
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feDropShadow
                          dx="0"
                          dy="0"
                          stdDeviation="4"
                          floodColor={a.color}
                          floodOpacity="0.75"
                        />
                      </filter>
                    ))}
                  </defs>
                  <circle
                    cx="100"
                    cy="100"
                    r="82"
                    fill="none"
                    stroke={t.donutRing}
                    strokeWidth="1"
                  />
                  {archArcsToDraw.map((a) => (
                    <path
                      key={a.name}
                      d={donutArcPath(100, 100, 82, 54, a.start, a.end)}
                      fill={a.color}
                      opacity={
                        archDisplayed && archDisplayed !== a.name ? 0.35 : 1
                      }
                      filter={
                        archDisplayed === a.name
                          ? `url(#glow-${a.name})`
                          : undefined
                      }
                      style={{
                        cursor: "pointer",
                        transition:
                          "opacity 0.25s ease, filter 0.25s ease, transform 0.25s ease",
                        transform:
                          archDisplayed === a.name
                            ? "scale(1.015)"
                            : "scale(1)",
                        transformOrigin: "100px 100px",
                      }}
                      onMouseEnter={() => setActive(a.name)}
                      onMouseLeave={() => setActive(null)}
                      onClick={() =>
                        setPinned((p) => (p === a.name ? null : a.name))
                      }
                    />
                  ))}
                </svg>
                <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                  <span className={"text-3xl font-bold " + t.textPrimary}>
                    {archActiveSeg ? (
                      archTotal > 0 ? (
                        <AnimatedNumber
                          value={(archActiveSeg.value / archTotal) * 100}
                          suffix="%"
                        />
                      ) : (
                        "0%"
                      )
                    ) : (
                      <AnimatedNumber value={archTotal} />
                    )}
                  </span>
                  <span
                    className={
                      "mt-1 max-w-[7rem] text-center text-[11px] uppercase tracking-wide " +
                      t.textMuted
                    }
                  >
                    {archActiveSeg ? archActiveSeg.name : "Total Results"}
                  </span>
                </div>
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto">
                {archArcs.map((a, idx) => (
                  <div
                    key={a.name}
                    onMouseEnter={() => setActive(a.name)}
                    onMouseLeave={() => setActive(null)}
                    onClick={() =>
                      setPinned((p) => (p === a.name ? null : a.name))
                    }
                    className={
                      "ta-stagger flex cursor-pointer items-center justify-between gap-8 rounded-xl px-3 py-2 transition-colors " +
                      (archDisplayed === a.name ? t.surface : t.surfaceHover) +
                      (pinned === a.name ? " ring-1 ring-inset" : "")
                    }
                    style={{
                      animationDelay: `${idx * 45}ms`,
                      ...(pinned === a.name
                        ? { boxShadow: `inset 0 0 0 1px ${a.color}55` }
                        : {}),
                    }}
                  >
                    <div className="flex items-center gap-2.5">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: a.color,
                          boxShadow: `0 0 8px ${a.color}aa`,
                        }}
                      />
                      <span
                        className={
                          "text-sm " +
                          (isDark ? "text-slate-200" : "text-slate-700")
                        }
                      >
                        {a.name}
                      </span>
                    </div>
                    <span
                      className={
                        "text-sm font-semibold tabular-nums " +
                        (isDark ? "text-slate-300" : "text-slate-600")
                      }
                    >
                      {archTotal > 0 ? (
                        <AnimatedNumber value={a.value + "/" + archTotal} />
                      ) : (
                        "--"
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <ResultsFooterCount count={archTotal} />
          </>
        )}
      </CardShell>
    );
  }

  if (uiConfig.type === "dimensional") {
    const hasData = testEntry?.percentages || testEntry?.averages; // was: testEntry?.averages only
    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        {Header}
        {!hasData ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
            <p className={"text-sm font-medium " + t.textMuted}>
              No results recorded yet for this test.
            </p>
          </div>
        ) : (
          <>
            {uiConfig.display === "zoneGauge" ? (
              <ChakraZoneBars
                fields={uiConfig.fields}
                counts={testEntry.counts}
                totalTests={testEntry.totalTests}
                thresholds={uiConfig.thresholds}
              />
            ) : uiConfig.display === "radial" ? (
              // UNCHANGED — excluded per spec, still reads averages (arch counts)
              <ArchetypeRadarPanel
                fields={uiConfig.fields}
                values={testEntry.averages}
                total={testEntry.totalTests}
              />
            ) : (
              <DimensionalResultsGrid
                fields={uiConfig.fields ?? uiConfig.dimensions}
                values={
                  testEntry.counts ?? testEntry.averages ?? testEntry.buckets
                }
                max={
                  testEntry.counts
                    ? testEntry.totalTests || 1
                    : (uiConfig.max ?? 25)
                }
              />
            )}
            <ResultsFooterCount count={testEntry?.totalTests} />
          </>
        )}
      </CardShell>
    );
  }
  if (uiConfig.type === "categorical") {
    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        {Header}
        {!testEntry?.percentages ? (
          <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
            <p className={"text-sm font-medium " + t.textMuted}>
              No results recorded yet for this test.
            </p>
          </div>
        ) : (
          <>
            <CategoricalResultsPanel
              fields={uiConfig.fields}
              values={testEntry.percentages}
              counts={testEntry.counts}
              dominant={testEntry.dominant}
              theme={isDark ? "dark" : "light"}
            />
            <ResultsFooterCount count={testEntry?.totalTests} />
          </>
        )}
      </CardShell>
    );
  }

  if (uiConfig.type === "qualitative") {
    return (
      <CardShell accent="teal" className="ta-card-lift flex flex-col">
        {Header}
        <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
          <p className={"text-sm font-medium " + t.textMuted}>
            {testEntry?.totalTests ?? 0} response
            {testEntry?.totalTests === 1 ? "" : "s"} recorded — no numeric score
            for this test.
          </p>
        </div>
        <ResultsFooterCount count={testEntry?.totalTests} label="response" />
      </CardShell>
    );
  }
  if (uiConfig.type === "subscale") {
  return (
    <CardShell accent="teal" className="ta-card-lift flex flex-col">
      {Header}
      {!testEntry?.categories ? (
        <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
          <p className={"text-sm font-medium " + t.textMuted}>
            No results recorded yet for this test.
          </p>
        </div>
      ) : (
        <>
          <SubscaleResultsPanel
            categories={testEntry.categories}
            totalTests={testEntry.totalTests}
          />
          <ResultsFooterCount count={testEntry?.totalTests} />
        </>
      )}
    </CardShell>
  );
}

  // --- everything below here is for Scored Tests --
  if (activeData && activeData?.totalTests?.length == 0) {
    return <CardShell>No data available.</CardShell>;
  }

  const totalValue = activeData.testCounts.reduce(
    (s, x) => s + (x.value ?? 0),
    0,
  );

  // the new branch and the old donut branch can render it.

  let cursor = 0;
  const arcs = activeData.testCounts.map((seg) => {
    const isDefense = uiConfig?.type === "defense";
    const segAmount = isDefense ? (seg.count ?? 0) : seg.value; // NEW: use count for defense
    const denom = isDefense ? activeData.totalTests || 1 : total; // NEW: denom is totalTests, not 100
    const start = (cursor / denom) * 360;
    cursor += segAmount;
    const end = (cursor / denom) * 360;
    return { ...seg, start, end, type: isDefense ? "defense" : "" };
  });

  const displayed = active ?? pinned;
  const activeSeg = arcs.find((a) => a.name === displayed);

  const arcsToDraw = (() => {
    const withValue = arcs.filter((a) => (a.value ?? 0) > 0);
    if (!displayed) return withValue;
    const rest = withValue.filter((a) => a.name !== displayed);
    const activeOne = withValue.filter((a) => a.name === displayed);
    return [...rest, ...activeOne];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  })();

  return (
    <CardShell
      accent="teal"
      className="ta-card-lift flex flex-col h-full justify-between"
    >
      {Header}

      {!isSupportedTest &&
      !isChakraTest &&
      uiConfig?.type !== "defense" &&
      uiConfig?.type !== "comparative" ? (
        <div className="flex flex-1 flex-col items-center justify-center py-16 text-center">
          <p className={"text-sm font-medium " + t.textMuted}>
            Results for this test aren't available yet.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-1 flex-col items-center justify-center gap-6 sm:flex-row">
            <div className="relative h-56 w-56 shrink-0">
              <svg viewBox="0 0 200 200" className="h-full w-full">
                <defs>
                  {arcsToDraw.map((a) => (
                    <filter
                      key={`glow-${a.name}`}
                      id={`glow-${a.name}`}
                      x="-50%"
                      y="-50%"
                      width="200%"
                      height="200%"
                    >
                      <feDropShadow
                        dx="0"
                        dy="0"
                        stdDeviation="4"
                        floodColor={a.color}
                        floodOpacity="0.75"
                      />
                    </filter>
                  ))}
                </defs>
                <circle
                  cx="100"
                  cy="100"
                  r="82"
                  fill="none"
                  stroke={t.donutRing}
                  strokeWidth="1"
                />
                {arcsToDraw.map((a) => (
                  <path
                    key={a.name}
                    d={donutArcPath(100, 100, 82, 54, a.start, a.end - 0.001)}
                    fill={a.color}
                    opacity={displayed && displayed !== a.name ? 0.35 : 1}
                    filter={
                      displayed === a.name ? `url(#glow-${a.name})` : undefined
                    }
                    style={{
                      cursor: "pointer",
                      transition:
                        "opacity 0.25s ease, filter 0.25s ease, transform 0.25s ease",
                      transform:
                        displayed === a.name ? "scale(1.015)" : "scale(1)",
                      transformOrigin: "100px 100px",
                    }}
                    onMouseEnter={() => setActive(a.name)}
                    onMouseLeave={() => setActive(null)}
                    onClick={() =>
                      setPinned((p) => (p === a.name ? null : a.name))
                    }
                  />
                ))}
              </svg>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <span className={"text-3xl font-bold " + t.textPrimary}>
                  {activeSeg ? (
                    total > 0 ? (
                      <AnimatedNumber
                        value={
                          activeSeg?.type == "defense"
                            ? Math.round((activeSeg.count / total) * 100) // NEW: count-based %, not raw value
                            : (activeSeg.value / total) * 100
                        }
                        suffix="%"
                      />
                    ) : (
                      "0%"
                    )
                  ) : (
                    <AnimatedNumber value={total} />
                  )}
                </span>
                <span
                  className={
                    "mt-1 max-w-[7rem] text-center text-[11px] uppercase tracking-wide " +
                    t.textMuted
                  }
                >
                  {activeSeg ? activeSeg.name : "Total Results"}
                </span>
              </div>
            </div>

            <div className="flex w-full flex-col gap-3 sm:w-auto">
              {arcs.map((a, idx) => (
                <div
                  key={a.name}
                  onMouseEnter={() => setActive(a.name)}
                  onMouseLeave={() => setActive(null)}
                  onClick={() =>
                    setPinned((p) => (p === a.name ? null : a.name))
                  }
                  className={
                    "ta-stagger flex cursor-pointer items-center justify-between gap-8 rounded-xl px-3 py-2 transition-colors " +
                    (displayed === a.name ? t.surface : t.surfaceHover) +
                    (pinned === a.name ? " ring-1 ring-inset" : "")
                  }
                  style={{
                    animationDelay: `${idx * 45}ms`,
                    ...(pinned === a.name
                      ? { boxShadow: `inset 0 0 0 1px ${a.color}55` }
                      : {}),
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{
                        backgroundColor: a.color,
                        boxShadow: `0 0 8px ${a.color}aa`,
                      }}
                    />
                    <span
                      className={
                        "text-sm " +
                        (isDark ? "text-slate-200" : "text-slate-700")
                      }
                    >
                      {a.name}
                    </span>
                  </div>
                  <span
                    className={
                      "text-sm font-semibold tabular-nums " +
                      (isDark ? "text-slate-300" : "text-slate-600")
                    }
                  >
                    {a?.type === "defense" ? (
                      `${a.count ?? 0}/${activeData.totalTests}`
                    ) : total > 0 ? (
                      <AnimatedNumber
                        value={`${a.value}/${total}`}
                        suffix="%"
                      />
                    ) : (
                      "--"
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <ResultsFooterCount count={total} />
        </>
      )}
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Mode toggle — "Standard Assessments" vs "Work Personality Test"
// ---------------------------------------------------------------------------

function ResultsModeToggle({ mode, setMode, code }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  const allOptions = [
    { key: "personality", label: "Work Personality Test", icon: Sparkles, testonly:false },
    { key: "standard", label: "Standard Assessments", icon: Layers,testonly:false  },
    { key: "iit", label: "IIT Test", icon: GraduationCap , testonly:true },
  ];

  const options =
    code === "AIIMS"
      ? allOptions.filter((opt) => opt.key === "standard")
      : code ==="CEWPTEST" ||code ==="WAPTEST" || code=="HEALOTEST1"  ? allOptions :   allOptions.filter((opt) => opt.testonly === false )  ;

  return (
    <div
      className={
        "relative inline-flex items-center rounded-full border p-1 " +
        t.pillBorder +
        " " +
        t.surfaceSoft
      }
      role="tablist"
      aria-label="Results mode"
    >
      {options.map((opt) => {
        const activeOpt = mode === opt.key;
        const Icon = opt.icon;
        return (
          <button
            key={opt.key}
            role="tab"
            aria-selected={activeOpt}
            onClick={() => setMode(opt.key)}
            className={
              "relative z-10 flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-300 active:scale-95 " +
              (activeOpt ? "text-white" : t.textMuted + " " + t.surfaceHover)
            }
            style={
              activeOpt
                ? {
                    background:
  opt.key === "personality"
    ? `linear-gradient(90deg, ${BRAND.purple}, ${BRAND.pink})`
    : opt.key === "iit"
      ? `linear-gradient(90deg, ${BRAND.pink}, ${BRAND.teal})`
      : `linear-gradient(90deg, ${BRAND.teal}, ${BRAND.purple})`,
                  }
                : undefined
            }
          >
            <Icon size={13} />
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function ArchetypeLeaderboard({ archetypes, totalParticipants, loading }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hovered, setHovered] = useState(null);

  const ranked = useMemo(() => {
    return [...(archetypes ?? [])]
      .sort((a, b) => b.count - a.count)
      .map((a, i) => ({
        ...a,
        color: a.color ?? WP_PALETTE[i % WP_PALETTE.length].left,
        pct: totalParticipants > 0 ? (a.count / totalParticipants) * 100 : 0,
      }));
  }, [archetypes, totalParticipants]);

  return (
    <CardShell accent="purple" className="ta-card-lift flex flex-col">
      <div className="mb-1 flex items-center justify-between">
        <h2 className={"text-xl font-bold " + t.textPrimary}>
          Personality Archetypes
        </h2>
        <span
          className={
            "flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-medium " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.textMuted
          }
        >
          <Sparkles size={12} />
          Distribution
        </span>
      </div>
      <p className={"mb-6 text-sm " + t.textMuted}>
        Which work personality each participant landed on
      </p>

      <div className="flex flex-1 flex-col gap-5">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-base font-medium text-gray-500">Loading...</p>
          </div>
        ) : ranked?.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center py-10 text-gray-500">
            <Sparkles className="mb-3 h-12 w-12 opacity-40" />
            <p className="text-lg font-semibold">No results yet</p>
            <p className="mt-1 text-center text-sm text-gray-400">
              Archetypes will appear once participants complete the test.
            </p>
          </div>
        ) : (
          ranked?.map((a, idx) => {
            const isHovered = hovered === a.name;
            return (
              <div
                key={a.name}
                className="ta-hoverable ta-stagger group cursor-default"
                style={{ animationDelay: `${idx * 45}ms` }}
                onMouseEnter={() => setHovered(a.name)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className="mb-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{
                        backgroundColor: a.color,
                        boxShadow: `0 0 8px ${a.color}aa`,
                      }}
                    />
                    <span
                      className={
                        "font-medium transition-colors " +
                        (isHovered
                          ? t.textPrimary
                          : isDark
                            ? "text-slate-200"
                            : "text-slate-700")
                      }
                    >
                      {a.name}
                    </span>
                  </div>

                  <span
                    className="font-semibold tabular-nums"
                    style={{ color: a.color }}
                  >
                    {a.count}/{totalParticipants}
                    <span className={"ml-1.5 " + t.textMuted}>
                      (<AnimatedNumber value={a.pct} suffix="%" />)
                    </span>
                  </span>
                </div>

                <div
                  className={
                    "h-2.5 w-full overflow-hidden rounded-full transition-colors " +
                    t.track
                  }
                >
                  <div
                    className="ta-premium-bar h-full rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${a.pct}%`,
                      background: `linear-gradient(90deg, ${a.color}99, ${a.color})`,
                      boxShadow: isHovered ? `0 0 14px ${a.color}88` : "none",
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </CardShell>
  );
}

function TraitSpectrumBar({ trait, total, colorLeft, colorRight }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [hoverPole, setHoverPole] = useState(null);
  const [pinnedPole, setPinnedPole] = useState(null);

  const leftPct = total > 0 ? (trait.left.count / total) * 100 : 0;
  const midPct = total > 0 ? (trait.mid.count / total) * 100 : 0;
  const rightPct = total > 0 ? (trait.right.count / total) * 100 : 0;

  const poles = [
    {
      key: "left",
      label: trait.left.label,
      count: trait.left.count,
      color: colorLeft,
      pct: leftPct,
      align: "left",
    },
    {
      key: "mid",
      label: trait.mid.label,
      count: trait.mid.count,
      color: WP_HEX.neutral,
      pct: midPct,
      align: "center",
    },
    {
      key: "right",
      label: trait.right.label,
      count: trait.right.count,
      color: colorRight,
      pct: rightPct,
      align: "right",
    },
  ];

  const activePole = hoverPole ?? pinnedPole;
  const leading = poles.reduce((a, b) => (b.count > a.count ? b : a));

  return (
    <div
      className="ta-hoverable group cursor-default"
      onMouseLeave={() => setHoverPole(null)}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <span
          className={
            "text-[11px] font-semibold uppercase tracking-wide transition-colors " +
            t.textMuted
          }
        >
          {leading.key !== "mid" ? `Leans ${leading.label}` : "Balanced"}
        </span>
      </div>

      <div className="mb-1.5 flex items-end justify-between gap-2 text-[13px]">
        <span
          onMouseEnter={() => setHoverPole("left")}
          onClick={() => setPinnedPole((p) => (p === "left" ? null : "left"))}
          className={
            "cursor-pointer font-medium transition-opacity " +
            (isDark ? "text-slate-200" : "text-slate-700") +
            (activePole && activePole !== "left" ? " opacity-40" : "")
          }
        >
          {trait.left.label}
          <span
            className="ml-1 font-semibold tabular-nums"
            style={{ color: colorLeft }}
          >
            <AnimatedNumber value={trait.left.count} />
          </span>
        </span>

        <span
          onMouseEnter={() => setHoverPole("mid")}
          onClick={() => setPinnedPole((p) => (p === "mid" ? null : "mid"))}
          className={
            "cursor-pointer text-center font-medium " +
            t.textMuted +
            (activePole && activePole !== "mid" ? " opacity-40" : "")
          }
        >
          {trait.mid.label}
          <span className="ml-1 font-semibold tabular-nums">
            <AnimatedNumber value={trait.mid.count} />
          </span>
        </span>

        <span
          onMouseEnter={() => setHoverPole("right")}
          onClick={() => setPinnedPole((p) => (p === "right" ? null : "right"))}
          className={
            "cursor-pointer text-right font-medium transition-opacity " +
            (isDark ? "text-slate-200" : "text-slate-700") +
            (activePole && activePole !== "right" ? " opacity-40" : "")
          }
        >
          <span
            className="mr-1 font-semibold tabular-nums"
            style={{ color: colorRight }}
          >
            <AnimatedNumber value={trait.right.count} />
          </span>
          {trait.right.label}
        </span>
      </div>

      <div
        className={
          "flex h-3 w-full overflow-hidden rounded-full transition-colors " +
          t.track
        }
      >
        {poles.map((p, i) => (
          <div
            key={p.key}
            onMouseEnter={() => setHoverPole(p.key)}
            onClick={() =>
              setPinnedPole((prev) => (prev === p.key ? null : p.key))
            }
            style={{
              width: `${p.pct}%`,
              background: p.color,
              opacity: activePole && activePole !== p.key ? 0.35 : 1,
              boxShadow:
                activePole === p.key ? `0 0 10px ${p.color}aa` : "none",
              marginLeft: i === 0 ? 0 : "2px",
            }}
            className="ta-premium-bar h-full cursor-pointer transition-all duration-200"
          />
        ))}
      </div>
    </div>
  );
}

function TraitCategoryCard({ category, total, accent, colors, index = 0 }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  return (
    <CardShell
      accent={accent}
      className="ta-card-lift ta-stagger flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className={"text-base font-bold " + t.textPrimary}>
          {category.title}
        </h3>
        <span
          className={
            "rounded-full border px-2.5 py-0.5 text-[10px] font-medium " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.textMuted
          }
        >
          {category.traits.length} traits
        </span>
      </div>

      <div className="flex flex-col gap-5">
        {category.traits.map((trait, i) => (
          <TraitSpectrumBar
            key={trait.left.label + i}
            trait={trait}
            total={total}
            colorLeft={colors.left}
            colorRight={colors.right}
          />
        ))}
      </div>
    </CardShell>
  );
}

function WorkPersonalityResults({
  workPersonalityData,
  loading,
  error,
  pulse,
  pulseKey,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  const data = workPersonalityData?.data;
  const total = data?.totalParticipants ?? 0;
  const archetypes = data?.archetypes ?? [];
  const categories = data?.categories ?? [];

  const topArchetype = useMemo(() => {
    if (!archetypes.length) return "--";
    return [...archetypes].sort((a, b) => b.count - a.count)[0].name;
  }, [archetypes]);

  if (error) {
    return <CardShell>Unable to load Work Personality results.</CardShell>;
  }
  console.log("pulse fired", pulse);

  return (
    <div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="relative">
          <StatPill
            icon={Users}
            tint={BRAND.purple}
            value={
              loading ? "..." : total ? <AnimatedNumber value={total} /> : "--"
            }
            label="Participants tested"
          />
          {pulse && (
            <span key={pulseKey} className="ta-plus-one-badge">
              +1
            </span>
          )}
        </div>
        <StatPill
          icon={Trophy}
          tint={BRAND.pink}
          value={loading ? "..." : topArchetype}
          label="Most common archetype"
        />
        <StatPill
          icon={Layers}
          tint={BRAND.teal}
          value={
            loading ? (
              "..."
            ) : categories.length ? (
              <AnimatedNumber value={categories.length} />
            ) : (
              "--"
            )
          }
          label="Categories"
        />
      </div>

      <div className="mb-6">
        <ArchetypeLeaderboard
          archetypes={archetypes}
          totalParticipants={total}
          loading={loading}
        />
      </div>

      {loading ? (
        <CardShell>Loading...</CardShell>
      ) : categories.length === 0 ? (
        <CardShell className="flex flex-col items-center justify-center py-10 text-gray-500">
          <ArrowLeftRight className="mb-3 h-12 w-12 opacity-40" />
          <p className="text-lg font-semibold">No trait data yet</p>
          <p className="mt-1 text-center text-sm text-gray-400">
            Category breakdowns will appear once results come in.
          </p>
        </CardShell>
      ) : (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {categories.map((cat, i) => (
            <TraitCategoryCard
              key={cat.id ?? cat.title}
              category={cat}
              total={total}
              accent={CARD_ACCENTS[i % CARD_ACCENTS.length]}
              colors={WP_PALETTE[i % WP_PALETTE.length]}
              index={i}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section — heading + toggle + mode-specific content
// ---------------------------------------------------------------------------

function TestAnalyticsSection({
  testData,
  severityData,
  loading,
  error,
  selectedTest,
  setSelectedTest,
  mode,
  setMode,
  workPersonalityData,
  workPersonalityLoading,
  workPersonalityError,
  bumpParticipants,
  iitData,
  iitLoading,
  iitError,
  code,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const resultsRef = useRef(null);

  // Clicking a test in "Most Taken Tests" / "All Tests" selects it in the
  // dropdown above and scrolls the results box into view.
  const handleSelectTest = (name) => {
    setSelectedTest(name);
    resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const personalityRef = useRef(null);
  const [pendingHighlight, setPendingHighlight] = useState(null);
  const [pulseParticipants, setPulseParticipants] = useState(false);
  const [pulseKey, setPulseKey] = useState(0); // ADD

  const handleTestNotification = useCallback(
    ({ redirectTo, isWorkPersonality } = {}) => {
      if (isWorkPersonality) {
        setMode("personality");
        bumpParticipants?.();
        setPulseKey((k) => k + 1);
        setPulseParticipants(true);
        setTimeout(() => setPulseParticipants(false), 1600);

        // NEW: give the mode switch a tick to render, then scroll
        requestAnimationFrame(() => {
          setTimeout(() => {
            personalityRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }, 60);
        });
        return;
      }
      setMode("standard");
      setPendingHighlight(redirectTo);
    },
    [setMode, bumpParticipants],
  );

  useEffect(() => {
    const handler = (e) => handleTestNotification(e.detail || {});
    window.addEventListener("ta-test-notification", handler);
    return () => window.removeEventListener("ta-test-notification", handler);
  }, [handleTestNotification]);

  useEffect(() => {
    const raw = sessionStorage.getItem("pendingTestNotification");
    if (!raw) return;
    sessionStorage.removeItem("pendingTestNotification");
    try {
      handleTestNotification(JSON.parse(raw));
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <PremiumStyles />
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div
            className="mb-2 flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em]"
            style={{ color: BRAND.purple }}
          >
            <TrendingUp size={14} />
            Analytics
          </div>
          <h1
            className={
              "text-4xl font-extrabold tracking-tight " + t.textPrimary
            }
          >
            Test Analytics
          </h1>
          <p className={"mt-2 text-sm " + t.textMuted}>
            A snapshot of how your organization is engaging with Healo's
            self-assessments and well-being tools.
          </p>
        </div>

        <ResultsModeToggle mode={mode} setMode={setMode} code={code} />
      </div>

      {mode === "iit" ? (
  <IitWellbeingResults data={iitData} loading={iitLoading} error={iitError} />
) :
      mode === "personality" ? (
        <WorkPersonalityResults
          workPersonalityData={workPersonalityData}
          loading={workPersonalityLoading}
          error={workPersonalityError}
          pulse={pulseParticipants}
          pulseKey={pulseKey}
        />
      ) : (
        <>
          <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <StatPill
              icon={ClipboardList}
              tint="#8b7cf6"
              value={
                loading ? (
                  "..."
                ) : testData?.data?.summary?.[0]?.totalTestsTaken != null ? (
                  <AnimatedNumber
                    value={testData.data.summary[0].totalTestsTaken}
                  />
                ) : (
                  "--"
                )
              }
              label="Total tests taken"
            />
            <StatPill
              icon={Users}
              tint="#34d9b8"
              value={
                loading ? (
                  "..."
                ) : testData?.data?.summary?.[0]?.uniqueUsersCount != null ? (
                  <AnimatedNumber
                    value={testData.data.summary[0].uniqueUsersCount}
                  />
                ) : (
                  "--"
                )
              }
              label="Unique participants"
            />
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <MostTakenTests
              testData={testData}
              loading={loading}
              selectedTest={selectedTest}
              onSelectTest={handleSelectTest}
            />
            {/* Step 1: Add flex flex-col here */}
            <div ref={resultsRef} className="flex flex-col">
              <TestResultsDonut
                Severity2={severityData}
                testData={testData}
                loading={loading}
                error={error}
                selectedTest={selectedTest}
                setSelectedTest={setSelectedTest}
              />
            </div>
          </div>
          <div className="py-10">
            <MostTakenTests
              testData={testData}
              loading={loading}
              viewall={true}
              selectedTest={selectedTest}
              onSelectTest={handleSelectTest}
              pendingHighlight={pendingHighlight}
              onHighlightConsumed={() => setPendingHighlight(null)}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default function TestAnalytics({
  testData: testDataProp,
  severityData: severityDataProp,
  workPersonalityData: workPersonalityDataProp,
  loading: loadingProp,
  error: errorProp,
  wrapInPageShell = true,
  showFooter = true,
  showBg = true,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { code } = useAuth();

  const isControlled = testDataProp !== undefined;
  const isPersonalityControlled = workPersonalityDataProp !== undefined;

  const [mode, setMode] = useState("personality"); // "standard" | "personality"
  useEffect(() => {
    if (code === "AIIMS" && mode !== "standard") {
      setMode("standard");
    }
  }, [code, mode]);

  const [iitData, setIitData] = useState();
const [iitLoading, setIitLoading] = useState(false);
const [iitError, setIitError] = useState(null);

useEffect(() => {
  if (mode !== "iit") return;
  (async () => {
    try {
      setIitLoading(true);
      setIitError(null);
      const res = await fetch(`${serverUrl}api/v1/iit/iit-wellbeing-results/${code}`);
      if (!res.ok) throw new Error("Failed to fetch data");
      const json = await res.json();
      setIitData(json.data);
    } catch (e) {
      console.error(e);
      setIitError(e.message);
    } finally {
      setIitLoading(false);
    }
  })();
}, [code, mode]);

  const [testData, setTestData] = useState({});
  const [severityData, setSeverityData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // No default "all" option anymore — this starts empty and gets set to the
  // first available test once test data has loaded (see effect below).
  const [selectedTest, setSelectedTest] = useState("");

  const [workPersonalityData, setWorkPersonalityData] = useState();
  const [workPersonalityLoading, setWorkPersonalityLoading] = useState(false);
  const [workPersonalityError, setWorkPersonalityError] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const bumpParticipants = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Auto-select the first test once test data is available, since the
  // "All Tests" option has been removed from the results dropdown.
  useEffect(() => {
    const source = isControlled ? testDataProp : testData;
    const firstTestName = source?.data?.testCounts?.[0]?.testName;
    if (!selectedTest && firstTestName) {
      setSelectedTest(firstTestName);
    }
  }, [isControlled, testDataProp, testData, selectedTest]);

  useEffect(() => {
    if (isControlled) return; // parent is supplying data, skip own fetch

    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const [testsRes, severityRes] = await Promise.all([
          fetch(`${serverUrl}api/v1/users/test-taken/${code}`),
          fetch(
            `${serverUrl}api/v1/users/test-score-status/${code}${
              selectedTest
                ? `?testname=${encodeURIComponent(selectedTest)}`
                : ""
            }`,
          ),
        ]);

        if (!testsRes.ok || !severityRes.ok) {
          throw new Error("Failed to fetch data");
        }

        const tests = await testsRes.json();
        const severity = await severityRes.json();

        setTestData(tests);
        setSeverityData(severity);
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [code, isControlled, selectedTest]);

  // Only fetch Work Personality data once the user actually toggles to it,
  // to avoid an extra request on every page load.
  useEffect(() => {
    if (isPersonalityControlled) return;
    if (mode !== "personality") return;

    const load = async () => {
      try {
        setWorkPersonalityLoading(true);
        setWorkPersonalityError(null);

        const res = await fetch(
          `${serverUrl}api/v1/users/work-personality-results/${code}`,
        );
        if (!res.ok) throw new Error("Failed to fetch data");
        const json = await res.json();
        setWorkPersonalityData(json.data);
      } catch (err) {
        console.warn(
          "Work Personality endpoint not ready yet, using mock data.",
          err,
        );
      } finally {
        setWorkPersonalityLoading(false);
      }
    };

    load();
  }, [code, mode, isPersonalityControlled, refreshKey]);

  const section = (
    <TestAnalyticsSection
      testData={isControlled ? testDataProp : testData}
      severityData={isControlled ? severityDataProp : severityData}
      loading={isControlled ? loadingProp : loading}
      error={isControlled ? errorProp : error}
      selectedTest={selectedTest}
      setSelectedTest={setSelectedTest}
      mode={mode}
      setMode={setMode}
      workPersonalityData={
        isPersonalityControlled ? workPersonalityDataProp : workPersonalityData
      }
      workPersonalityLoading={workPersonalityLoading}
      workPersonalityError={workPersonalityError}
      bumpParticipants={bumpParticipants}
      iitData={iitData}
      iitLoading={iitLoading}
      iitError={iitError}
      code={code}
    />
  );

  if (!wrapInPageShell) return section;

  return (
    <div className={"min-h-screen w-full " + (showBg ? t.pageWash : "")}>
      <div className={"w-full  py-8  " + t.textPrimary}>
        <div className="mx-auto max-w-6xl">
          {section}
          {showFooter ? (
            <div
              className={`mt-2 flex flex-wrap items-center justify-center gap-1 px-4 pb-8 text-center text-xs ${t.textFaint}`}
            >
              <span>Made with</span>
              <span style={{ color: BRAND.pink }}>💗</span>
              <span>
                by Infiheal — private, judgment-free insights for a healthier
                organization.
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export { TestAnalyticsSection, WorkPersonalityResults ,IitWellbeingResults};
